/**
 * BinaryReader — the only path in the disk-image cluster that touches a File.
 *
 * Disk image files routinely run 4-50 GB; loading them into memory is not
 * an option. This wrapper exposes a sector-cached, async, DataView-style
 * read surface so every parser (ISO9660 / UDF / MBR / GPT / VHD / ...) reads
 * by sector or by byte range without ever materialising the whole file.
 *
 * Cache: LRU of up to 256 × 2 KB sectors = 512 KB ceiling. A reader never
 * holds more than this in RAM regardless of image size. Cross-sector reads
 * stitch sectors on demand.
 *
 * Surface: window.__DiskImage.BinaryReader
 *   var r = new BinaryReader(file);
 *   await r.sector(lba)              → Uint8Array(2048)
 *   await r.bytes(off, len)          → Uint8Array(len)
 *   await r.u8(off)                  → number 0..255
 *   await r.u16le(off)               → number
 *   await r.u32le(off)               → number
 *   await r.u64le(off)               → number (loses precision >2^53)
 *   await r.u16be / u32be            → big-endian variants
 *   await r.utf16be(off, len)        → string
 *   await r.utf16le(off, len)        → string
 *   await r.ascii(off, len)          → string
 *   await r.scan(magic, from, to)    → byte offset of first match or -1
 *   r.streamRange(off, len)          → ReadableStream<Uint8Array>
 *   r.size                           → file.size
 */
(function (w) {
    'use strict';
    w.__DiskImage = w.__DiskImage || {};
    if (w.__DiskImage.BinaryReader) return;

    var SECTOR = 2048;
    var MAX_SECTORS = 256; // 512 KB hard cap

    function BinaryReader(file, opts) {
        if (!file || typeof file.slice !== 'function') {
            throw new Error('BinaryReader: requires a File or Blob');
        }
        this.file = file;
        this.size = file.size;
        this.sectorSize = (opts && opts.sectorSize) || SECTOR;
        this._cache = new Map(); // lba → Uint8Array (insertion order = LRU order)
        this._inflight = new Map(); // lba → Promise<Uint8Array>
        this._maxSectors = (opts && opts.maxSectors) || MAX_SECTORS;
    }

    BinaryReader.prototype._fetchSector = function (lba) {
        var hit = this._cache.get(lba);
        if (hit) {
            // Refresh LRU position
            this._cache.delete(lba);
            this._cache.set(lba, hit);
            return Promise.resolve(hit);
        }
        var pending = this._inflight.get(lba);
        if (pending) return pending;

        var off = lba * this.sectorSize;
        if (off >= this.size) return Promise.resolve(new Uint8Array(0));
        var end = Math.min(this.size, off + this.sectorSize);
        var self = this;
        var p = this.file.slice(off, end).arrayBuffer().then(function (ab) {
            var u8 = new Uint8Array(ab);
            // Pad short tail to sector size so callers can always read 2048
            if (u8.byteLength < self.sectorSize) {
                var padded = new Uint8Array(self.sectorSize);
                padded.set(u8, 0);
                u8 = padded;
            }
            self._cache.set(lba, u8);
            self._inflight.delete(lba);
            // LRU evict
            while (self._cache.size > self._maxSectors) {
                var firstKey = self._cache.keys().next().value;
                self._cache.delete(firstKey);
            }
            return u8;
        }).catch(function (err) {
            self._inflight.delete(lba);
            throw err;
        });
        this._inflight.set(lba, p);
        return p;
    };

    BinaryReader.prototype.sector = function (lba) {
        return this._fetchSector(lba);
    };

    BinaryReader.prototype.bytes = function (off, len) {
        if (len <= 0) return Promise.resolve(new Uint8Array(0));
        if (off < 0 || off + len > this.size) {
            return Promise.reject(new RangeError('BinaryReader.bytes: out of bounds (' + off + '+' + len + ' > ' + this.size + ')'));
        }
        var firstLba = Math.floor(off / this.sectorSize);
        var lastLba = Math.floor((off + len - 1) / this.sectorSize);
        var sectorSize = this.sectorSize;
        var self = this;

        // Hot path: single sector
        if (firstLba === lastLba) {
            return this._fetchSector(firstLba).then(function (s) {
                return s.subarray(off - firstLba * sectorSize, off - firstLba * sectorSize + len);
            });
        }

        // Cross-sector: fetch all and stitch
        var promises = [];
        for (var lba = firstLba; lba <= lastLba; lba++) promises.push(this._fetchSector(lba));
        return Promise.all(promises).then(function (sectors) {
            var out = new Uint8Array(len);
            var written = 0;
            for (var i = 0; i < sectors.length; i++) {
                var lba = firstLba + i;
                var sectorStart = lba * sectorSize;
                var sliceFrom = (i === 0) ? off - sectorStart : 0;
                var sliceLen = Math.min(sectorSize - sliceFrom, len - written);
                out.set(sectors[i].subarray(sliceFrom, sliceFrom + sliceLen), written);
                written += sliceLen;
            }
            return out;
        });
    };

    BinaryReader.prototype.u8 = function (off) {
        return this.bytes(off, 1).then(function (b) { return b[0]; });
    };
    BinaryReader.prototype.u16le = function (off) {
        return this.bytes(off, 2).then(function (b) { return b[0] | (b[1] << 8); });
    };
    BinaryReader.prototype.u16be = function (off) {
        return this.bytes(off, 2).then(function (b) { return (b[0] << 8) | b[1]; });
    };
    BinaryReader.prototype.u32le = function (off) {
        return this.bytes(off, 4).then(function (b) {
            return ((b[0] | (b[1] << 8) | (b[2] << 16) | (b[3] << 24)) >>> 0);
        });
    };
    BinaryReader.prototype.u32be = function (off) {
        return this.bytes(off, 4).then(function (b) {
            return (((b[0] << 24) | (b[1] << 16) | (b[2] << 8) | b[3]) >>> 0);
        });
    };
    BinaryReader.prototype.u64le = function (off) {
        return this.bytes(off, 8).then(function (b) {
            var lo = ((b[0] | (b[1] << 8) | (b[2] << 16) | (b[3] << 24)) >>> 0);
            var hi = ((b[4] | (b[5] << 8) | (b[6] << 16) | (b[7] << 24)) >>> 0);
            // Best-effort number; >Number.MAX_SAFE_INTEGER will round.
            return hi * 0x100000000 + lo;
        });
    };
    BinaryReader.prototype.u64be = function (off) {
        return this.bytes(off, 8).then(function (b) {
            var hi = (((b[0] << 24) | (b[1] << 16) | (b[2] << 8) | b[3]) >>> 0);
            var lo = (((b[4] << 24) | (b[5] << 16) | (b[6] << 8) | b[7]) >>> 0);
            return hi * 0x100000000 + lo;
        });
    };

    BinaryReader.prototype.ascii = function (off, len) {
        return this.bytes(off, len).then(function (b) {
            var out = '';
            for (var i = 0; i < b.length; i++) {
                if (b[i] === 0) break;
                out += String.fromCharCode(b[i]);
            }
            return out;
        });
    };
    BinaryReader.prototype.utf16be = function (off, len) {
        return this.bytes(off, len).then(function (b) {
            return new TextDecoder('utf-16be', { fatal: false }).decode(b);
        });
    };
    BinaryReader.prototype.utf16le = function (off, len) {
        return this.bytes(off, len).then(function (b) {
            return new TextDecoder('utf-16le', { fatal: false }).decode(b);
        });
    };

    /**
     * Find first occurrence of a magic byte sequence within [from, to).
     * Magic may be a Uint8Array, regular array, or string (latin-1 byte values).
     */
    BinaryReader.prototype.scan = function (magic, from, to) {
        if (typeof magic === 'string') {
            var enc = new Uint8Array(magic.length);
            for (var k = 0; k < magic.length; k++) enc[k] = magic.charCodeAt(k) & 0xFF;
            magic = enc;
        } else if (Array.isArray(magic)) {
            magic = new Uint8Array(magic);
        }
        if (!(magic instanceof Uint8Array) || magic.length === 0) {
            return Promise.resolve(-1);
        }
        from = Math.max(0, from | 0);
        to = Math.min(this.size, to | 0);
        if (to <= from) return Promise.resolve(-1);

        var sectorSize = this.sectorSize;
        var self = this;
        var firstLba = Math.floor(from / sectorSize);
        var lastLba = Math.floor((to - 1) / sectorSize);
        var carry = new Uint8Array(0);

        function step(lba) {
            if (lba > lastLba) return -1;
            return self._fetchSector(lba).then(function (s) {
                var sectorStart = lba * sectorSize;
                var window = new Uint8Array(carry.length + s.length);
                window.set(carry, 0);
                window.set(s, carry.length);
                var searchFrom = (lba === firstLba) ? (from - sectorStart) : 0;
                // Boyer-Horspool would be faster but ISO magic bytes are short — naive is fine
                var limit = window.length - magic.length;
                for (var i = searchFrom; i <= limit; i++) {
                    var ok = true;
                    for (var j = 0; j < magic.length; j++) {
                        if (window[i + j] !== magic[j]) { ok = false; break; }
                    }
                    if (ok) {
                        // Translate window-relative offset to absolute file offset
                        var carryOffset = sectorStart - carry.length;
                        var abs = carryOffset + i;
                        if (abs + magic.length <= to) return abs;
                    }
                }
                // Carry overlap = magic.length - 1 to catch boundary-spanning matches
                var carryStart = window.length - (magic.length - 1);
                if (carryStart < 0) carryStart = 0;
                carry = window.slice(carryStart);
                return step(lba + 1);
            });
        }
        return step(firstLba);
    };

    /**
     * ReadableStream of [off, off+len) in 1 MB chunks. Backpressure-friendly.
     */
    BinaryReader.prototype.streamRange = function (off, len) {
        if (off < 0 || off + len > this.size) {
            throw new RangeError('streamRange: out of bounds');
        }
        var CHUNK = 1024 * 1024;
        var pos = off;
        var end = off + len;
        var file = this.file;
        return new ReadableStream({
            pull: function (controller) {
                if (pos >= end) { controller.close(); return; }
                var chunkEnd = Math.min(end, pos + CHUNK);
                return file.slice(pos, chunkEnd).arrayBuffer().then(function (ab) {
                    controller.enqueue(new Uint8Array(ab));
                    pos = chunkEnd;
                }).catch(function (err) {
                    controller.error(err);
                });
            }
        });
    };

    BinaryReader.prototype.dispose = function () {
        this._cache.clear();
        this._inflight.clear();
    };

    BinaryReader.prototype.cacheStats = function () {
        return { sectors: this._cache.size, maxSectors: this._maxSectors, sectorSize: this.sectorSize };
    };

    w.__DiskImage.BinaryReader = BinaryReader;
})(window);
